import {debounce} from 'lodash';
import React, {Component, createRef, RefObject} from 'react';
import styled, {css} from 'styled-components';

import {globalWindow} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {withSize, WithSizeProps} from '../../../libs/shared/ui/src/hooks/sizeHooks';
import {addThemedScrollbarStyles, stretch} from '../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../libs/shared/ui/src/theme/userThemeConsumer';
import {RenderProfiler, RenderTypesEnum} from '../../../srcCommon/services/performanceMetrics/renderProfiler';
import {LocalPreferencesSizes, LocalPreferencesSizesEnum} from '../../models/api/localPreferencesModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../services/tracking';
import {SizeUpdater} from '../../store/data/localPreferences/localPreferencesActions';
import {maxCellsPanelWidth, minCellsPanelWidth} from '../../store/panels/cells/helpers/cellsPanelPathHelpers';
import {InboxesParameters} from '../../store/panels/inboxes/parameters/inboxesParameters';
import {findParametersInboxId} from '../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {buildParametersPath} from '../../store/panels/inboxes/parameters/inboxesParametersPathHelpers';
import {
  CellsPanelId,
  InboxesPanelId,
  makeCellsPanelId,
  makeViewerPanelId,
} from '../../store/panels/panelStateBase';
import {maxLeftPanelWidth} from '../app/leftPanelHelpers';
import {GridItem} from '../common/grid';
import {ResizeSeparator} from '../common/resizeSeparator';
import {DarkSidebarThemeProvider} from '../common/theme/darkSidebarThemeProvider';
import {ExpandedDarkThemeProvider} from '../common/theme/expandedDarkThemeProvider';
import {LensesAssigneeSearchInfoBubble} from '../infoBubbles/lensesAssigneeSearchInfoBubble';
import {LensesTagSearchInfoBubble} from '../infoBubbles/lensesTagSearchInfoBubble';
import {InboxesBanners} from './banners/inboxesBanners';
import {CellsContainer} from './cells/cellsContainer';
import {CreateTagRuleDialogContainer} from './createTagRuleDialogContainer';
import {MassArchiveDialogManagerContainer} from './dialogs/massArchiveDialogManagerContainer';
import {InboxesDragDropLogger} from './inboxesDragDropLogger';
import {OpenConversation} from './openConversation';
import {PaletteCoordinator} from './palette/paletteCoordinator';
import {maxSidePanelWidth, minSidePanelWidth, sidePanelSwitcherWidth} from './side/sidePanel';
import {SidePanelContainer} from './side/sidePanelContainer';
import {InboxesTopbarContainer} from './topbar/inboxesTopbarContainer';
import {RequiredTaggingCoordinator} from './viewer/conversation/safetyNets/requiredTaggingCoordinator';
import {SafetyNetsProvider} from './viewer/conversation/safetyNets/safetyNetsContext';
import {viewerMinWidth} from './viewer/conversation/viewerConstants';
import {DockedViewerContainer} from './viewer/dockedViewerContainer';
import {EmptyInboxesContainer} from './viewer/empty/emptyInboxesContainer';
import {WorkspacesEditorProviderContainer} from './workspaces/editor/context/workspacesEditorProviderContainer';
import {WorkspacesEditorDialogs} from './workspaces/editor/dialogs/workspacesEditorDialogs';
import {WorkspacesEditorContainer} from './workspaces/editor/workspacesEditorContainer';
import {WorkspacesContainer} from './workspaces/workspacesContainer';

declare global {
  interface Window {
    clearOffline?: () => void;
  }
}

/*
 * Constants.
 */

const minWorkspacesWidth = 158;
const maxWorkspacesWidth = maxLeftPanelWidth;
const separatorsLeftOfSidePanelWidth = 2;

const minWorkspacesWidthWithBanner = 240;

/*
 * Props.
 */

export interface InboxesProps extends WithSizeProps {
  wrapperRef: RefObject<HTMLDivElement>;
  panelId: InboxesPanelId;
  sizes: LocalPreferencesSizes;
  updateSize: SizeUpdater;
  reopenOfflineDraftMessages: Handler;
  clearOfflineDrafts: Handler;
  parameters: InboxesParameters;
  onParametersChange: HandlerOf<InboxesParameters>;
  isEditingWorkspaces: boolean;
  hasCellsFilters: boolean;
  hasP1AutoRec: boolean;
  shouldShowP1AutoRecBanner: boolean;
  shouldShowEditModeHelpTip: boolean;
  starredTagId: number;
}

/*
 * Style.
 */

const StyledGridDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: auto auto 1fr;
  grid-template-rows: 100%;

  color: ${(p) => p.theme.text};
  ${addThemedScrollbarStyles()};

  min-height: 250px;
  height: 100%;

  // Setting z-index to establish new stacking context.
  z-index: 0;
`;

const TransparentOverlay = styled.div`
  display: grid;
  grid-column: 2/4;
  grid-row: 1;

  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1;
`;

const StyledViewerDiv = styled.div`
  ${(p) => css`
    grid-row: 1;
    grid-column: 3;

    display: grid;
    grid-template-rows: min-content min-content minmax(0, 1fr);
    grid-template-columns: auto auto minmax(${viewerMinWidth}px, 1fr) auto;
  `}
`;

const StyledStretchGridItem = styled(GridItem)`
  ${stretch()}
`;

const StyledSidePanel = styled.div`
  grid-row: 1 / -1;
  grid-column: 4;
  min-width: ${sidePanelSwitcherWidth}px;
  display: flex;
  align-items: stretch;
`;

/*
 * Component.
 */

interface InboxesState {
  workspacesWidth: number;
  cellsWidth: number;
  sideWidth: number;
  isResizingSidePanel: boolean;
}

class InboxesComponent extends Component<InboxesProps, InboxesState> {
  constructor(props: InboxesProps) {
    super(props);
    this.state = {
      workspacesWidth: props.sizes[LocalPreferencesSizesEnum.WORKSPACES],
      cellsWidth: props.sizes[LocalPreferencesSizesEnum.CELLS],
      sideWidth: props.sizes[LocalPreferencesSizesEnum.SIDE],
      isResizingSidePanel: false,
    };
    this.workspaceRef = createRef<HTMLDivElement>();
  }

  private readonly workspaceRef: RefObject<HTMLDivElement>;

  /*
   * Event handlers.
   */

  private readonly onWorkspacesWidthChange: HandlerOf<number> = (workspacesWidth) => {
    const boundedWorkspacesWidth = Math.min(300, Math.max(170, workspacesWidth));
    this.setState({workspacesWidth: boundedWorkspacesWidth});
    this.saveWorkspaceWidthDebounce(boundedWorkspacesWidth);
  };

  private readonly onCellsWidthChange: HandlerOf<number> = (cellsWidth) => {
    this.setState({cellsWidth});
    this.saveCellsWidthDebounce(cellsWidth);
  };

  private readonly onSideWidthChange: HandlerOf<number> = (sideWidth) => {
    // We ensure the new size is within bounds as the value may come from a plugin.
    const boundedSideWidth = Math.min(maxSidePanelWidth, Math.max(minSidePanelWidth, sideWidth));
    this.setState({sideWidth: boundedSideWidth});
    this.saveSideWidthDebounce(boundedSideWidth);
  };

  private readonly onSideIsResizingChange: HandlerOf<boolean> = (isResizingSidePanel) => {
    this.setState({isResizingSidePanel});
  };

  private readonly saveWorkspaceWidthDebounce = debounce((newWidth: number) => {
    this.props.updateSize(LocalPreferencesSizesEnum.WORKSPACES, newWidth);
  }, 1000);

  private readonly saveCellsWidthDebounce = debounce((newWidth: number) => {
    trackInterfaceEvent(InterfaceEventsEnum.P2_CELL_DRAG, {
      if_resized_smaller: newWidth < this.props.sizes[LocalPreferencesSizesEnum.CELLS],
    });

    this.props.updateSize(LocalPreferencesSizesEnum.CELLS, newWidth);
  }, 1000);

  private readonly saveSideWidthDebounce = debounce((newWidth: number) => {
    this.props.updateSize(LocalPreferencesSizesEnum.SIDE, newWidth);
  }, 1000);

  /*
   * Lifecycle.
   */

  componentDidMount() {
    // Will reopen composed drafts that were never saved anywhere.
    this.props.reopenOfflineDraftMessages();
    globalWindow.clearOffline = this.props.clearOfflineDrafts;
  }

  componentWillUnmount() {
    delete globalWindow.clearOffline;
  }

  /*
   * Render.
   */

  render() {
    const {panelId, parameters, onParametersChange, isEditingWorkspaces} = this.props;
    const {workspacesWidth, cellsWidth} = this.state;
    const selectedInboxId = findParametersInboxId(parameters);
    const gridRowOffset = 1;
    const cellsPanelId = makeCellsPanelId(panelId);

    return (
      <ExpandedDarkThemeProvider>
        <SafetyNetsProvider>
          <RequiredTaggingCoordinator />
          <StyledGridDiv ref={this.props.wrapperRef}>
            <DarkSidebarThemeProvider>
              <RenderProfiler type={RenderTypesEnum.INBOXES_WORKSPACES}>
                {this.renderWorkspaces(selectedInboxId)}
              </RenderProfiler>
              {isEditingWorkspaces && <TransparentOverlay style={{left: workspacesWidth}} />}
            </DarkSidebarThemeProvider>
            <StyledViewerDiv>
              {this.renderCellsAndViewerPanel(
                cellsPanelId,
                onParametersChange,
                panelId,
                gridRowOffset,
                cellsWidth,
              )}
              <RenderProfiler type={RenderTypesEnum.INBOXES_SIDE_PANEL}>
                {this.maybeRenderSidePanel()}
              </RenderProfiler>
            </StyledViewerDiv>
            <UserThemeConsumer>
              {(theme) => (
                <ResizeSeparator
                  column={2}
                  size={workspacesWidth}
                  minSize={minWorkspacesWidth}
                  maxSize={maxWorkspacesWidth}
                  onSizeChange={this.onWorkspacesWidthChange}
                  color={theme.separator}
                />
              )}
            </UserThemeConsumer>
            <DarkSidebarThemeProvider>
              <InboxesBanners panelId={panelId} />
            </DarkSidebarThemeProvider>
            <OpenConversation />
            <CreateTagRuleDialogContainer />
            <MassArchiveDialogManagerContainer />
          </StyledGridDiv>
          <PaletteCoordinator parameters={parameters} />
          <InboxesDragDropLogger />
        </SafetyNetsProvider>
      </ExpandedDarkThemeProvider>
    );
  }

  private renderCellsAndViewerPanel(
    cellsPanelId: CellsPanelId,
    onParametersChange: HandlerOf<InboxesParameters>,
    panelId: InboxesPanelId,
    gridRowOffset: number,
    cellsWidth: number,
  ) {
    const topbar = (
      <StyledStretchGridItem column="1 / -2" row={1}>
        <InboxesTopbarContainer cellsPanelId={cellsPanelId} inboxesPanelId={panelId} isInPopup={false} />
      </StyledStretchGridItem>
    );

    return (
      <UserThemeConsumer>
        {(theme) => (
          <CellsContainer
            panelId={cellsPanelId}
            buildParametersPath={buildParametersPath}
            onParametersChange={onParametersChange}
          >
            {(cells, {isEmpty, didBecomeEmpty}) => (
              <>
                {isEmpty ? (
                  <>
                    {this.props.hasCellsFilters ? topbar : null}
                    <StyledStretchGridItem
                      column="1 / 4"
                      row={this.props.hasCellsFilters ? '2 / 4' : '1 / 4'}
                    >
                      <EmptyInboxesContainer panelId={cellsPanelId} didBecomeEmpty={didBecomeEmpty} />
                    </StyledStretchGridItem>
                  </>
                ) : (
                  <>
                    {topbar}
                    <StyledStretchGridItem column={1} row={gridRowOffset + 2} style={{width: cellsWidth}}>
                      <RenderProfiler type={RenderTypesEnum.INBOXES_CELLS}>{cells}</RenderProfiler>
                    </StyledStretchGridItem>
                    <StyledStretchGridItem column={3} row={gridRowOffset + 2}>
                      <RenderProfiler type={RenderTypesEnum.INBOXES_VIEWER}>
                        <DockedViewerContainer
                          cellsPanelId={cellsPanelId}
                          panelId={makeViewerPanelId(panelId)}
                        />
                      </RenderProfiler>
                    </StyledStretchGridItem>
                    <ResizeSeparator
                      column={2}
                      row={gridRowOffset + 2}
                      size={cellsWidth}
                      minSize={minCellsPanelWidth}
                      maxSize={maxCellsPanelWidth}
                      onSizeChange={this.onCellsWidthChange}
                      color={theme.greys.shade50}
                    />
                  </>
                )}
              </>
            )}
          </CellsContainer>
        )}
      </UserThemeConsumer>
    );
  }

  private renderWorkspaces(selectedInboxId: number | undefined) {
    const {panelId, isEditingWorkspaces, hasP1AutoRec, shouldShowP1AutoRecBanner, shouldShowEditModeHelpTip} =
      this.props;
    const {workspacesWidth} = this.state;

    const computedEditModeWorkspacesWidth =
      hasP1AutoRec && shouldShowEditModeHelpTip
        ? Math.max(workspacesWidth, minWorkspacesWidthWithBanner)
        : workspacesWidth;

    if (isEditingWorkspaces) {
      return (
        <WorkspacesEditorProviderContainer panelId={panelId}>
          <StyledGridDiv ref={this.props.wrapperRef}>
            <StyledStretchGridItem column={1} style={{width: computedEditModeWorkspacesWidth}}>
              <WorkspacesEditorContainer
                panelId={panelId}
                size={computedEditModeWorkspacesWidth}
                shouldShowHelpTip={shouldShowEditModeHelpTip}
                onWorkspacesWidthChange={this.onWorkspacesWidthChange}
              />
            </StyledStretchGridItem>
          </StyledGridDiv>
          <WorkspacesEditorDialogs />
        </WorkspacesEditorProviderContainer>
      );
    }

    const computedWorkspacesWidth =
      hasP1AutoRec && shouldShowP1AutoRecBanner
        ? Math.max(workspacesWidth, minWorkspacesWidthWithBanner)
        : workspacesWidth;

    return (
      <StyledStretchGridItem column={1} style={{width: computedWorkspacesWidth}} ref={this.workspaceRef}>
        <WorkspacesContainer
          panelId={panelId}
          size={computedWorkspacesWidth}
          selectedInboxId={selectedInboxId}
        />
        <LensesTagSearchInfoBubble targetRef={this.workspaceRef} panelId={panelId} />
        <LensesAssigneeSearchInfoBubble targetRef={this.workspaceRef} panelId={panelId} />
      </StyledStretchGridItem>
    );
  }

  private maybeRenderSidePanel() {
    const {panelId, size} = this.props;
    const {workspacesWidth, cellsWidth, sideWidth, isResizingSidePanel} = this.state;

    const totalWidth = (size && size.width) || 0;
    const mainPanelsWidth = workspacesWidth + cellsWidth + viewerMinWidth;
    const leftOfSidePanelWidth = mainPanelsWidth + separatorsLeftOfSidePanelWidth;
    const sidePanelOverlayWidthThreshold = totalWidth - leftOfSidePanelWidth;

    return (
      <StyledSidePanel>
        <SidePanelContainer
          cellsPanelId={makeCellsPanelId(panelId)}
          panelId={makeViewerPanelId(panelId)}
          isResizing={isResizingSidePanel}
          overlayWidthTreshold={sidePanelOverlayWidthThreshold}
          openWidth={sideWidth}
          onIsResizingChange={this.onSideIsResizingChange}
          onRequestWidthChange={this.onSideWidthChange}
        />
      </StyledSidePanel>
    );
  }
}

export const Inboxes = withSize(InboxesComponent);
